import { releaseFeatures } from "@/config/releaseFeatures";
import { useHomepageExperiment } from "@/services/homepageExperiment";
import { useUser } from "@/redux/user";
import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import debounce from "lodash.debounce";
import { gaEvent } from "@/lib/ga";
import {
  validateEmail,
  validatePassword,
  validateUsername,
} from "@/util/validate";
import { setLoadingBar } from "@/redux/siteData";
import { api } from "@/services/api";
import { errorCodes } from "@chesshotel/constants";
import {
  useRegister,
  setUsername,
  setEmail,
  setPassword,
  setNews,
  setEmailFree,
  setRecoveryCode,
  setStep,
  setSignedUpWithEmail,
  setSocialToken,
  setSocialAccountType,
  setUsernameError,
  setEmailError,
  setPasswordError,
  RegisterStep,
} from "@/redux/register";
import { setUserData, setLoading } from "@/redux/signIn";
import {
  inlineSignInOrWillCreateGoogleUser as inlineSignInOrWillCreateGoogleUserAction,
  inlineSignInOrWillCreateFB as inlineSignInOrWillCreateFBAction,
  signInFBInline as signInFBInlineAction,
  inlineSignInOrWillCreateAppleUser as inlineSignInOrWillCreateAppleUserAction,
  signInAppleWebInline as signInAppleWebInlineAction,
  initializeGoogleButton as initializeGoogleButtonAction,
} from "./registerActions";

export type { RegisterStep };

export interface UseRegisterFormOptions {
  onStepChange?: (step: RegisterStep) => void;
  onExistingUserSignIn?: () => void;
}

export function useRegisterForm(options: UseRegisterFormOptions = {}) {
  const { onStepChange, onExistingUserSignIn } = options;
  const router = useRouter();
  const { isGuest, displayName } = useUser();
  const homepageExperience = useHomepageExperiment();
  const {
    guestElo,
    progressKey,
    guestUsername,
    username,
    email,
    password,
    news,
    step,
    signedUpWithEmail,
    emailFree: storedEmailFree,
    recoveryCode,
    socialToken,
    socialAccountType,
    usernameError,
    emailError,
    passwordError,
  } = useRegister();

  const emailFree = releaseFeatures.emailFreeAccounts && storedEmailFree;
  useEffect(() => {
    if (!releaseFeatures.emailFreeAccounts && storedEmailFree) setEmailFree(false);
  }, [storedEmailFree]);

  // Track if we've already processed the redirect from sign-in
  const hasProcessedRedirect = useRef(false);
  const latestEmailRef = useRef(email);
  const latestUsernameRef = useRef(username);
  useEffect(() => {
    latestUsernameRef.current = username;
  }, [username]);

  useEffect(() => {
    latestEmailRef.current = email;
  }, [email]);

  // Notify parent of step changes
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Internal step setter that also notifies parent
  const updateStep = useCallback(
    (newStep: RegisterStep) => {
      setStep(newStep);
      onStepChange?.(newStep);
    },
    [onStepChange, setStep]
  );

  // Prefill the current guest identity for every registration entry point.
  const guestNameInitialized = useRef(false);
  useEffect(() => {
    if (!homepageExperience.value || guestNameInitialized.current || !isGuest || !displayName) return;
    guestNameInitialized.current = true;
    const candidate =
      username || guestUsername?.replace(/^G\*/, "") || displayName;
    if (!username) {
      latestUsernameRef.current = candidate;
      setUsername(candidate);
    }
    let current = true;
    void validateUsername(candidate, router.locale).then((error) => {
      if (current && latestUsernameRef.current === candidate)
        setUsernameError(error || null);
    });
    return () => {
      current = false;
    };
  }, [homepageExperience.value, isGuest, displayName, router.locale]);
  useEffect(() => {
    gaEvent({ category: "RegisterWithEmail", action: "StartCreateAccount" });
  }, []);

  // Handle redirect from sign-in page when user doesn't exist
  useEffect(() => {
    // Only process once and if we're still on the form step
    if (hasProcessedRedirect.current || step !== "form") return;

    const { accountType, credential } = router.query;
    if (
      accountType === "google" &&
      credential &&
      typeof credential === "string"
    ) {
      // User was redirected from sign-in because they don't have an account
      // We already know the user doesn't exist (got 204 from backend)
      // So we can directly proceed to username step without calling backend again
      hasProcessedRedirect.current = true;
      setSocialToken(credential);
      setSocialAccountType("google");
      updateStep("socialUsername");

      // Clean up the query params from URL
      router.replace("/register", undefined, { shallow: true });
    }
  }, [router.query, step]);

  // Validation functions
  function validateRegistrationPassword(value: string) {
    return emailFree && (!value || value.length < 8 || value.length > 128)
      ? "ERROR_ACCOUNT_PASSWORD"
      : emailFree
      ? undefined
      : validatePassword(value);
  }
  const debouncedValidateUsername = useCallback(
    debounce(async (val: string) => {
      const error = await validateUsername(val, router.locale);
      if (latestUsernameRef.current === val) setUsernameError(error || null);
    }, 700),
    [router.locale]
  );

  const debouncedValidatePassword = useCallback(
    debounce(async (val: string) => {
      setPasswordError(validateRegistrationPassword(val));
    }, 700),
    [emailFree]
  );

  useEffect(
    () => () => debouncedValidatePassword.cancel(),
    [debouncedValidatePassword]
  );

  useEffect(
    () => () => debouncedValidateUsername.cancel(),
    [debouncedValidateUsername]
  );

  // Form change handlers
  function emailChange(val: string) {
    latestEmailRef.current = val;
    setEmail(val);
    setEmailError(null);
  }

  async function emailBlur(val: string) {
    const error = await validateEmail(val);
    if (latestEmailRef.current === val) {
      setEmailError(error);
    }
  }

  async function usernameChange(val: string) {
    latestUsernameRef.current = val;
    setUsername(val);
    setUsernameError(null);
    debouncedValidateUsername(val);
  }

  function passwordChange(val: string) {
    setPassword(val);
    debouncedValidatePassword(val);
  }

  // Account creation
  async function validateCreateAccount() {
    const passwordErr = validateRegistrationPassword(password);
    const emailErr = emailFree ? undefined : await validateEmail(email);
    const usernameErr = await validateUsername(username, router.locale);

    setPasswordError(passwordErr);
    setEmailError(emailErr);
    setUsernameError(usernameErr);

    if (!passwordErr && !emailErr && !usernameErr) {
      createAccountRequest();
    }
  }

  function createAccountRequest() {
    setLoadingBar(true);

    const lang = router.locale;
    const elo = guestElo;
    const buildTarget = "web";

    api
      .post(
        "/users",
        {
          username,
          email: emailFree ? undefined : email,
          emailFree,
          password,
          lang,
          country: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
          elo,
          news: emailFree ? 0 : news,
          buildTarget,
          progressKey,
        },
        {
          validateStatus: function (status) {
            return status < 400;
          },
        }
      )
      .then(async (response) => {
        if (response.data) {
          setRecoveryCode(response.data.recoveryCode || null);
          setSignedUpWithEmail(!emailFree);
          await setUserData(response.data);
          updateStep("joined");
          setPassword("");
          gaEvent({
            category: emailFree ? "RegisterWithoutEmail" : "RegisterWithEmail",
            action: "CreateAccount",
          });
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .catch((error) => {
        if (error?.response?.status === 429) {
          setUsernameError("ERROR_AUTH_RATE_LIMIT");
        } else if (error?.response?.data === "ERROR_ACCOUNT_PASSWORD") {
          setPasswordError("ERROR_ACCOUNT_PASSWORD");
          updateStep("form");
        } else if (
          error?.response?.data === errorCodes.ERROR_USERNAME_NOT_AVAILABLE
        ) {
          setUsernameError(errorCodes.ERROR_USERNAME_NOT_AVAILABLE);
        } else if (
          error?.response?.data === errorCodes.ERROR_EMAIL_NOT_AVAILABLE
        ) {
          setEmailError(errorCodes.ERROR_EMAIL_NOT_AVAILABLE);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .then(() => {
        setLoadingBar(false);
      });
  }

  async function advanceToUsernameStep() {
    const passwordErr = validateRegistrationPassword(password);
    const emailErr = emailFree ? undefined : await validateEmail(email);
    setPasswordError(passwordErr);
    setEmailError(emailErr);
    if (!passwordErr && !emailErr) {
      updateStep("username");
    }
  }

  // Social sign-in handlers - wrap actions with callbacks
  const callbacks = useCallback(
    () => ({
      onExistingUserSignIn,
      onStepChange: updateStep,
    }),
    [onExistingUserSignIn, updateStep]
  );

  function inlineSignInOrWillCreateFB(accessToken: string) {
    inlineSignInOrWillCreateFBAction(accessToken, callbacks());
  }

  function signInFBInline() {
    signInFBInlineAction(callbacks());
  }

  function inlineSignInOrWillCreateAppleUser(token: string) {
    inlineSignInOrWillCreateAppleUserAction(token, callbacks());
  }

  async function signInAppleWebInline() {
    await signInAppleWebInlineAction(callbacks());
  }

  function inlineSignInOrWillCreateGoogleUser(credential: string) {
    inlineSignInOrWillCreateGoogleUserAction(credential, callbacks());
  }

  const initializeGoogleButton = useCallback(() => {
    initializeGoogleButtonAction(step, callbacks());
  }, [step, callbacks]);

  useEffect(() => {
    // Only initialize Google button when we're on the form step
    if (step !== "form") return;

    // Initialize Google button when component mounts or when navigating to this page
    // Check if Google script is already loaded
    if ((window as any).google?.accounts?.id) {
      initializeGoogleButton();
    } else {
      // If not loaded yet, wait a bit and try again
      const checkInterval = setInterval(() => {
        if ((window as any).google?.accounts?.id) {
          clearInterval(checkInterval);
          initializeGoogleButton();
        }
      }, 100);

      // Cleanup after 5 seconds if script doesn't load
      setTimeout(() => clearInterval(checkInterval), 5000);
    }
  }, [step, router.locale, initializeGoogleButton]);

  async function createSocialAccount() {
    // Validate then create social user
    const err = await validateUsername(username, router.locale);
    setUsernameError(err);
    if (err) return;
    if (!socialToken || !socialAccountType) return;
    setLoadingBar(true);
    let request;
    if (socialAccountType === "facebook") {
      request = api.post("/users/facebook", {
        username,
        token: socialToken,
        progressKey,
        lang: router.locale,
      });
    } else if (socialAccountType === "apple") {
      request = api.post("/users/apple", {
        username,
        token: socialToken,
        progressKey,
        lang: router.locale,
      });
    } else {
      request = api.post("/auth/google/token", {
        credential: socialToken,
        username,
        progressKey,
        lang: router.locale,
        elo: guestElo,
      });
    }
    request
      .then((res) => {
        // Check for successful response (200 or 201) with data
        if (res.status === 200 && res?.data) {
          setSignedUpWithEmail(false);
          setUserData(res.data);
          setLoading(false);
          updateStep("joined");
        } else if (res.status === 200 && !res?.data) {
          console.warn("Response 200 but no data - might be an issue");
          // Still try to proceed if status is 200
          setLoading(false);
          updateStep("joined");
        }
      })
      .catch((error) => {
        if (error?.response?.data === errorCodes.ERROR_USERNAME_NOT_AVAILABLE) {
          setUsernameError(errorCodes.ERROR_USERNAME_NOT_AVAILABLE);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .then(() => setLoadingBar(false));
  }

  const failStyle = { border: "1px solid #ff4900" } as const;
  const usernameStyle = usernameError && failStyle;
  const emailStyle = emailError && failStyle;
  const passwordStyle = passwordError && failStyle;

  return {
    // State
    username,
    email,
    password,
    news,
    step,
    signedUpWithEmail,
    emailFree,
    recoveryCode,
    setEmailFree,
    usernameError,
    emailError,
    passwordError,
    usernameStyle,
    emailStyle,
    passwordStyle,
    guestElo,
    // Handlers
    setNews,
    emailChange,
    emailBlur,
    usernameChange,
    passwordChange,
    advanceToUsernameStep,
    validateCreateAccount,
    createSocialAccount,
    signInFBInline,
    signInAppleWebInline,
    initializeGoogleButton,
    inlineSignInOrWillCreateGoogleUser,
  };
}
